import clsx from 'clsx';
import type { ReactNode } from 'react';

import { renderIcon } from '../util';
import type { BadgeKind, BadgeProps, BadgeWeight } from './Badge';
import styles from './Badge.module.css';

/** Kind for {@link BadgeLabel} text styles; includes `default` for non-badge contexts (e.g. Status). */
export type BadgeLabelKind = BadgeKind | 'default';

type BadgeLabelProps = {
    /** Matches {@link BadgeProps} size; drives text scale. */
    size?: BadgeProps['size'];
    /** Badge kind or `default` for neutral label styling (no feature/plan mono styling). */
    kind: BadgeLabelKind;
    isNumber: boolean;
    children: ReactNode;
    /** Optional label font weight override. */
    weight?: BadgeWeight;
    /** Extra classes (e.g. text color); merged after base typography. */
    className?: string;
};

/**
 * Text label span used inside badges and related pill components; keeps typography aligned with {@link Badge}.
 */
export function BadgeLabel(props: BadgeLabelProps) {
    const { size, kind, isNumber, children, className, weight } = props;
    const isEmphasizedKind = [
        'basic',
        'premium',
        'ultimate',
        'downgraded',
        'alpha',
        'beta',
        'new',
    ].includes(kind);
    const resolvedWeight = weight ?? (isEmphasizedKind ? 'semibold' : undefined);

    return (
        <span
            className={clsx(
                'shrink-0 text-nowrap',
                className,
                isNumber && styles.numberLabel,
                {
                    xxsmall:
                        'text-[0.625rem] leading-(--sp-typography-ui-small-line-height) font-(--sp-typography-ui-small-font-weight)',
                    xsmall: 'text-ui-small',
                    small: 'text-ui-small',
                    medium: 'text-ui-base',
                }[size || 'small'],
                {
                    'font-mono uppercase': isEmphasizedKind,
                    'font-normal': resolvedWeight === 'regular',
                    'font-medium': resolvedWeight === 'medium',
                    'font-semibold': resolvedWeight === 'semibold',
                }
            )}
        >
            {children}
        </span>
    );
}

/**
 * BadgeContent is the base component to render the content of a badge.
 */
export function BadgeContent(
    props: Pick<BadgeProps, 'icon' | 'label' | 'size' | 'weight'> & {
        isIconOnly: boolean;
        isNumber: boolean;
        iconSize: number;
        kind: BadgeKind;
    }
) {
    const { icon, label, size, iconSize, isIconOnly, isNumber, kind, weight } = props;

    return (
        <>
            {icon
                ? renderIcon(icon, {
                      size: iconSize,
                      className: 'shrink-0',
                  })
                : null}
            {!isIconOnly ? (
                <BadgeLabel isNumber={isNumber} kind={kind} size={size} weight={weight}>
                    {label}
                </BadgeLabel>
            ) : null}
        </>
    );
}
